import React, { useState, useEffect } from 'react';
import {
  Card,
  Spin,
  message,
  Tag,
  Button,
  Modal,
  Calendar,
  Empty,
  Row,
  Col,
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';

dayjs.locale('es');

const AlumnoClases = () => {
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [modalVisible, setModalVisible] = useState(false);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(null);
  const [asistenciaModalVisible, setAsistenciaModalVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadTurnos();
  }, []);

  const loadTurnos = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/mi/turnos');
      if (response.ok) {
        const data = await response.json();
        setTurnos(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar las clases');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las clases');
    } finally {
      setLoading(false);
    }
  };

  const getDiaSemana = (dia) => {
    // La BD usa: 1=Lunes, 2=Martes, ..., 6=Sábado, 7=Domingo
    const dias = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    return dias[dia] || '';
  };

  const getDiaSemanaFromDate = (fecha) => {
    if (!fecha) return '';
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    // Asegurar que la fecha esté en formato YYYY-MM-DD
    let fechaObj = dayjs(fecha);
    if (!fechaObj.isValid()) {
      // Intentar parsear en otros formatos
      fechaObj = dayjs(fecha, 'DD/MM/YYYY');
    }
    if (!fechaObj.isValid()) {
      console.error('Fecha inválida:', fecha);
      return '';
    }
    const diaIndex = fechaObj.day(); // dayjs: 0=Domingo, 1=Lunes, ..., 6=Sábado
    return dias[diaIndex] || '';
  };

  const handleMarcarAsistencia = (turno) => {
    setTurnoSeleccionado(turno);
    // Si ya tenemos fecha_clase (viene del backend), usarla directamente
    if (turno.fecha_clase) {
      setFechaSeleccionada(turno.fecha_clase);
      setAsistenciaModalVisible(true);
    } else if (turno.tur_tipo === 'FIJO') {
      // Para turnos fijos sin fecha_clase, mostrar calendario para seleccionar fecha
      setModalVisible(true);
    } else {
      // Para eventos, usar la fecha del turno
      setFechaSeleccionada(turno.tur_fecha_especifica);
      setAsistenciaModalVisible(true);
    }
  };

  const handleConfirmarAsistencia = async (asiste) => {
    if (!turnoSeleccionado) {
      message.error('Error: turno no seleccionado');
      return;
    }

    // Para eventos, usar la fecha del turno si no hay fecha seleccionada
    const fechaAEnviar = fechaSeleccionada || turnoSeleccionado.tur_fecha_especifica;

    try {
      const response = await apiClient.put(
        `/mi/turnos/${turnoSeleccionado.tur_id}/asistencia`,
        {
          fecha: fechaAEnviar,
          asiste: asiste,
        }
      );

      if (response.ok) {
        message.success(asiste ? 'Asistencia confirmada' : 'No asistencia registrada');
        setModalVisible(false);
        setAsistenciaModalVisible(false);
        setTurnoSeleccionado(null);
        setFechaSeleccionada(null);
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al actualizar la asistencia');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al actualizar la asistencia');
    }
  };

  const onCalendarSelect = (date) => {
    if (date && date.isAfter(dayjs().subtract(1, 'day'))) {
      setFechaSeleccionada(date.format('YYYY-MM-DD'));
      setModalVisible(false);
      setAsistenciaModalVisible(true);
    } else {
      message.warning('No puede seleccionar fechas pasadas');
    }
  };

  const disabledDate = (current) => {
    return current && current < dayjs().startOf('day');
  };

  // Función para verificar si se puede modificar la asistencia (más de 12 horas antes)
  const puedeModificarAsistencia = (turno) => {
    if (!turno.fecha_clase && !turno.tur_fecha_especifica) {
      return false;
    }
    
    const fechaClase = turno.fecha_clase || turno.tur_fecha_especifica;
    const horaInicio = turno.tur_hora_desde || '00:00';
    
    // Combinar fecha y hora para obtener la fecha/hora completa de inicio de la clase
    const fechaHoraClase = dayjs(`${fechaClase} ${horaInicio}`, 'YYYY-MM-DD HH:mm');
    
    if (!fechaHoraClase.isValid()) {
      console.error('Fecha/hora inválida:', fechaClase, horaInicio);
      return false;
    }
    
    // Calcular la diferencia en horas
    const ahora = dayjs();
    const horasRestantes = fechaHoraClase.diff(ahora, 'hour', true);
    
    // Se puede modificar si faltan más de 12 horas
    return horasRestantes > 12;
  };

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <h1 style={{ 
          color: '#ffffff', 
          marginBottom: isMobile ? '16px' : '24px',
          fontSize: isMobile ? '20px' : '24px',
        }}>
          <CalendarOutlined style={{ marginRight: '8px' }} />
          Mis Clases
        </h1>

        <Spin spinning={loading}>
          {turnos.length === 0 ? (
            <Empty 
              description="No tienes clases asignadas" 
              style={{ marginTop: '40px' }}
            />
          ) : (
            <Row gutter={isMobile ? [12, 12] : [16, 16]}>
              {turnos.map((turno, index) => {
                const esFijo = turno.tur_tipo === 'FIJO';
                const esEvento = turno.tur_tipo === 'EVENTO';
                // Usar la fecha que viene del backend (fecha_clase_display)
                const fechaProxima = turno.fecha_clase_display || (esEvento ? dayjs(turno.tur_fecha_especifica).format('DD/MM/YYYY') : '');
                const fechaClase = turno.fecha_clase || turno.tur_fecha_especifica;
                // Calcular el día de la semana desde la fecha real (siempre usar la fecha, no el tur_dia_semana)
                const diaSemanaDisplay = fechaClase ? getDiaSemanaFromDate(fechaClase) : '';

                return (
                  <Col xs={24} sm={12} lg={8} key={turno.tur_id}>
                    <Card
                      style={{
                        background: 'linear-gradient(135deg, #2d2d2d 0%, #3a3a3a 100%)',
                        border: '1px solid #404040',
                        borderRadius: '12px',
                        height: '100%',
                        minHeight: isMobile ? '280px' : '320px',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                      }}
                      hoverable
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#666666';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#404040';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      bodyStyle={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        padding: isMobile ? '16px' : '20px',
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px',
                        flex: 1,
                      }}>
                        {/* Header con tipo de turno */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Tag 
                            color={esFijo ? 'blue' : 'purple'}
                            style={{ fontSize: isMobile ? '11px' : '12px' }}
                          >
                            {esFijo ? 'Fijo' : 'Evento'}
                          </Tag>
                          {turno.categoria_color ? (
                            <Tag 
                              color={turno.categoria_color}
                              style={{ fontSize: isMobile ? '11px' : '12px' }}
                            >
                              {turno.categoria_descripcion}
                            </Tag>
                          ) : (
                            <div style={{ width: '1px', height: '1px' }} />
                          )}
                        </div>

                        {/* Información de fecha y hora */}
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '8px',
                            color: '#ffffff',
                            fontSize: isMobile ? '16px' : '18px',
                            fontWeight: 600,
                          }}>
                            <CalendarOutlined />
                            {esFijo ? (
                              <span>{diaSemanaDisplay}</span>
                            ) : (
                              <span>{fechaProxima}</span>
                            )}
                          </div>
                          {esFijo && (
                            <div style={{ 
                              fontSize: isMobile ? '12px' : '13px', 
                              color: '#b0b0b0',
                              marginLeft: '24px',
                              marginBottom: '8px',
                              minHeight: isMobile ? '16px' : '18px',
                            }}>
                              Fecha: {fechaProxima}
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            color: '#e0e0e0',
                            fontSize: isMobile ? '13px' : '14px',
                            marginLeft: esFijo ? '24px' : '0',
                            minHeight: isMobile ? '20px' : '22px',
                          }}>
                            <ClockCircleOutlined />
                            {turno.tur_hora_desde} - {turno.tur_hora_hasta}
                          </div>
                        </div>

                        {/* Cancha - siempre mostrar */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          minHeight: isMobile ? '20px' : '22px',
                        }}>
                          <EnvironmentOutlined />
                          {turno.cancha_descripcion || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>Sin asignar</span>}
                        </div>

                        {/* Profesor - siempre mostrar */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          color: '#b0b0b0',
                          fontSize: isMobile ? '12px' : '13px',
                          minHeight: isMobile ? '20px' : '22px',
                        }}>
                          <UserOutlined />
                          {turno.profesor_nombre && turno.profesor_apellido ? (
                            <span>{turno.profesor_nombre} {turno.profesor_apellido}</span>
                          ) : (
                            <span style={{ fontStyle: 'italic', opacity: 0.6 }}>Sin asignar</span>
                          )}
                        </div>

                        {/* Espaciador flexible para empujar el botón hacia abajo */}
                        <div style={{ flex: 1 }} />

                        {/* Botón para marcar asistencia - siempre en la misma posición */}
                        {(() => {
                          const puedeModificar = puedeModificarAsistencia(turno);
                          
                          if (turno.va_a_asistir === false) {
                            // Ya está marcado como que no asistirá
                            return (
                              <div style={{
                                marginTop: 'auto',
                                padding: '8px',
                                background: '#2d2d2d',
                                border: '1px solid #404040',
                                borderRadius: '6px',
                                textAlign: 'center',
                                color: '#ff7875',
                                fontSize: isMobile ? '12px' : '13px',
                                minHeight: isMobile ? '32px' : '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}>
                                <CloseCircleOutlined style={{ marginRight: '4px' }} />
                                No asistirás a esta clase
                              </div>
                            );
                          }
                          
                          if (!puedeModificar) {
                            // No se puede modificar (faltan menos de 12 horas)
                            return (
                              <div style={{
                                marginTop: 'auto',
                                padding: '8px',
                                background: '#2d2d2d',
                                border: '1px solid #404040',
                                borderRadius: '6px',
                                textAlign: 'center',
                                color: '#b0b0b0',
                                fontSize: isMobile ? '11px' : '12px',
                                minHeight: isMobile ? '32px' : '36px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <ClockCircleOutlined />
                                  <span>No se puede modificar</span>
                                </div>
                                <div style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.8 }}>
                                  Faltan menos de 12 horas
                                </div>
                              </div>
                            );
                          }
                          
                          // Se puede modificar
                          return (
                            <Button
                              type="default"
                              icon={<CloseCircleOutlined />}
                              onClick={() => {
                                // Asegurar que tenemos la fecha_clase para el turno
                                const turnoConFecha = {
                                  ...turno,
                                  fecha_clase: fechaClase,
                                };
                                handleMarcarAsistencia(turnoConFecha);
                              }}
                              style={{
                                marginTop: 'auto',
                                background: '#404040',
                                borderColor: '#666666',
                                color: '#e0e0e0',
                                width: '100%',
                              }}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              No asistiré
                            </Button>
                          );
                        })()}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Spin>

        {/* Modal para seleccionar fecha (turnos fijos) */}
        <Modal
          title="Seleccionar fecha"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setTurnoSeleccionado(null);
          }}
          footer={null}
          width={isMobile ? '95%' : 400}
        >
          {turnoSeleccionado && (
            <div>
              <p style={{ color: '#e0e0e0', marginBottom: '16px' }}>
                Selecciona la fecha en la que no asistirás:
              </p>
              <Calendar
                fullscreen={false}
                onSelect={onCalendarSelect}
                disabledDate={disabledDate}
                locale={locale}
                style={{ background: '#2d2d2d' }}
              />
            </div>
          )}
        </Modal>

        {/* Modal para confirmar asistencia */}
        <Modal
          title="Confirmar asistencia"
          open={asistenciaModalVisible}
          onCancel={() => {
            setAsistenciaModalVisible(false);
            setTurnoSeleccionado(null);
            setFechaSeleccionada(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setAsistenciaModalVisible(false);
                setTurnoSeleccionado(null);
                setFechaSeleccionada(null);
              }}
            >
              Cancelar
            </Button>,
            <Button
              key="confirm"
              type="primary"
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleConfirmarAsistencia(false)}
            >
              Confirmar: No asistiré
            </Button>,
          ]}
          width={isMobile ? '95%' : 500}
        >
          {turnoSeleccionado && fechaSeleccionada && (
            <div>
              <p style={{ color: '#e0e0e0', marginBottom: '16px' }}>
                ¿Confirmas que <strong>NO asistirás</strong> a esta clase?
              </p>
              <div style={{ 
                background: '#1a1a1a', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '16px',
              }}>
                {turnoSeleccionado.tur_tipo === 'FIJO' && (
                  <div style={{ color: '#b0b0b0', fontSize: '13px', marginBottom: '4px' }}>
                    Día: {getDiaSemana(turnoSeleccionado.tur_dia_semana)}
                  </div>
                )}
                <div style={{ color: '#b0b0b0', fontSize: '13px', marginBottom: '4px' }}>
                  Fecha: {dayjs(fechaSeleccionada).format('DD/MM/YYYY')}
                </div>
                <div style={{ color: '#b0b0b0', fontSize: '13px', marginBottom: '4px' }}>
                  Hora: {turnoSeleccionado.tur_hora_desde} - {turnoSeleccionado.tur_hora_hasta}
                </div>
                {turnoSeleccionado.cancha_descripcion && (
                  <div style={{ color: '#b0b0b0', fontSize: '13px', marginBottom: '4px' }}>
                    Cancha: {turnoSeleccionado.cancha_descripcion}
                  </div>
                )}
                {turnoSeleccionado.profesor_nombre && (
                  <div style={{ color: '#b0b0b0', fontSize: '13px' }}>
                    Profesor: {turnoSeleccionado.profesor_nombre} {turnoSeleccionado.profesor_apellido}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AlumnoClases;


